import React, { useState, useRef, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Container, Typography, TextField, IconButton, Paper, Avatar, Button, CircularProgress, useTheme, Divider, Dialog, DialogTitle, DialogContent, DialogActions, Chip, Badge, Tooltip } from '@mui/material';
import { Send as SendIcon, Delete as DeleteIcon, Comment as CommentIcon, Chat as ChatIcon, Info as InfoIcon, CheckCircle as CheckCircleIcon, AutoFixHigh as AutoFixHighIcon, CloudUpload as CloudUploadIcon, Launch as LaunchIcon, ExpandMore as ExpandMoreIcon, ExpandLess as ExpandLessIcon, ZoomIn as ZoomInIcon, Close as CloseIcon, Image as ImageIcon } from '@mui/icons-material';
import { homologationChatApi, HomologationChatMessage, Comment, AgentInfo, PromptSuggestion } from '../utils/homologationChatApi';
import { detectFilesInText } from '../utils/fileDetection';
import { fileUploadApi } from '../utils/fileUploadApi';
import { splitMessageContent } from '../utils/messageUtils';
import toast from 'react-hot-toast';

// Função para processar formatação do WhatsApp usando as quebras que já vêm da API
const formatWhatsAppMessage = (text: string): React.ReactNode => {
  if (!text) return null;
  
  // Garantir que os \n sejam caracteres de nova linha reais (caso venham escapados)
  // Normalizar diferentes tipos de quebras de linha
  let normalizedText = text.replace(/\\n/g, '\n').replace(/\r\n/g, '\n').replace(/\r/g, '\n');
  
  // Dividir em linhas e renderizar cada uma como um bloco separado
  const lines = normalizedText.split('\n');
  const result: React.ReactNode[] = [];
  
  lines.forEach((line, lineIdx) => {
    const isLastLine = lineIdx === lines.length - 1;
    const isEmpty = !line.trim();
    const prevLineWasEmpty = lineIdx > 0 && !lines[lineIdx - 1].trim();
    
    // Se é linha vazia (duplo \n), adicionar espaço entre parágrafos
    if (isEmpty) {
      result.push(
        <Box
          key={`empty-${lineIdx}`}
          component="div"
          sx={{
            display: 'block',
            height: '0.6em',
            minHeight: '0.6em',
            width: '100%',
            lineHeight: 0,
            margin: 0,
            padding: 0,
          }}
        />
      );
      return;
    }
    
    // Processar formatações na linha
    const formattedLine = formatLine(line, `line-${lineIdx}`);
    
    // Detectar padrões para indentação
    const trimmedLine = line.trim();
    const isNumberedList = /^\d+\.\s+/.test(trimmedLine);
    const isSubItem = /^[\-\*]\s+/.test(trimmedLine);
    
    // Renderizar cada linha como um bloco separado
    result.push(
      <Box
        key={`line-${lineIdx}`}
        component="div"
        sx={{
          display: 'block',
          width: '100%',
          marginBottom: isLastLine ? '0' : (prevLineWasEmpty ? '0.6em' : '0.4em'),
          marginTop: 0,
          lineHeight: (isNumberedList || isSubItem) ? 1.7 : 1.8,
          paddingLeft: isSubItem ? '0.8em' : (isNumberedList ? '0.3em' : 0),
          paddingTop: 0,
          paddingBottom: 0,
        }}
      >
        {formattedLine}
      </Box>
    );
  });
  
  return <>{result}</>;
};

// Função auxiliar para processar uma linha de texto
const formatLine = (line: string, key: string): React.ReactNode => {
  if (!line) return null;
  
  const parts: React.ReactNode[] = [];
  let remaining = line;
  let partKey = 0;
  
  // Processar blocos de código primeiro (```code```)
  const codeBlockRegex = /```([\s\S]*?)```/g;
  let codeBlockMatch;
  const codeBlocks: Array<{ start: number; end: number; content: string }> = [];
  
  codeBlockRegex.lastIndex = 0;
  while ((codeBlockMatch = codeBlockRegex.exec(line)) !== null) {
    codeBlocks.push({
      start: codeBlockMatch.index,
      end: codeBlockMatch.index + codeBlockMatch[0].length,
      content: codeBlockMatch[1],
    });
  }
  
  // Se há blocos de código, processar separadamente
  if (codeBlocks.length > 0) {
    let lastIndex = 0;
    codeBlocks.forEach((block, idx) => {
      // Texto antes do bloco
      if (block.start > lastIndex) {
        const before = line.substring(lastIndex, block.start);
        parts.push(...formatInlineText(before, `${key}-before-${idx}`));
      }
      
      // Bloco de código
      parts.push(
        <Box
          key={`${key}-codeblock-${idx}`}
          component="pre"
          sx={{
            backgroundColor: 'rgba(0, 0, 0, 0.06)',
            padding: '10px 14px',
            borderRadius: '6px',
            margin: '6px 0',
            overflowX: 'auto',
            fontSize: '0.85em',
            fontFamily: '"Courier New", Courier, monospace',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            border: '1px solid rgba(0, 0, 0, 0.1)',
          }}
        >
          {block.content}
        </Box>
      );
      
      lastIndex = block.end;
    });
    
    // Texto após o último bloco
    if (lastIndex < line.length) {
      const after = line.substring(lastIndex);
      parts.push(...formatInlineText(after, `${key}-after`));
    }
    
    return <>{parts}</>;
  }
  
  // Sem blocos de código, processar formatações inline
  const inlineParts = formatInlineText(line, key);
  return inlineParts.length > 0 ? <>{inlineParts}</> : line;
};

// Função para processar formatações inline de forma simples
const formatInlineText = (text: string, baseKey: string): React.ReactNode[] => {
  if (!text) return [];
  
  const result: React.ReactNode[] = [];
  let processed = text;
  let keyCounter = 0;
  
  // Encontrar todas as formatações em ordem
  const formats: Array<{ start: number; end: number; type: string; content: string; url?: string }> = [];
  
  // Código inline (`code`)
  processed.replace(/`([^`]+)`/g, (match, content, offset) => {
    formats.push({ start: offset, end: offset + match.length, type: 'code', content });
    return match;
  });
  
  // Negrito (**texto**)
  processed.replace(/\*\*([^*]+)\*\*/g, (match, content, offset) => {
    formats.push({ start: offset, end: offset + match.length, type: 'bold', content });
    return match;
  });
  
  // Negrito simples (*texto*) - apenas se não for parte de **
  // Verificar manualmente para evitar lookbehind que pode não funcionar bem
  let boldSimpleRegex = /\*([^*\s][^*]*?)\*/g;
  let boldMatch;
  boldSimpleRegex.lastIndex = 0;
  while ((boldMatch = boldSimpleRegex.exec(processed)) !== null) {
    const start = boldMatch.index;
    const end = start + boldMatch[0].length;
    const before = processed.substring(Math.max(0, start - 2), start);
    const after = processed.substring(end, Math.min(processed.length, end + 2));
    
    // Verificar se não está dentro de **
    if (!before.includes('*') && !after.includes('*')) {
      // Verificar se não há conflito com formatação **
      const hasDoubleBold = formats.some(f => 
        f.type === 'bold' && (f.start <= start && f.end >= end)
      );
      if (!hasDoubleBold) {
        formats.push({ start, end, type: 'bold', content: boldMatch[1] });
      }
    }
  }
  
  // Itálico (_texto_)
  processed.replace(/_([^_\s][^_]*?)_/g, (match, content, offset) => {
    formats.push({ start: offset, end: offset + match.length, type: 'italic', content });
    return match;
  });
  
  // Riscado (~texto~)
  processed.replace(/~([^~]+)~/g, (match, content, offset) => {
    formats.push({ start: offset, end: offset + match.length, type: 'strike', content });
    return match;
  });
  
  // Links
  processed.replace(/(https?:\/\/[^\s]+)/g, (match, url, offset) => {
    formats.push({ start: offset, end: offset + match.length, type: 'link', content: url, url });
    return match;
  });
  
  // Remover sobreposições (manter apenas a primeira)
  formats.sort((a, b) => a.start - b.start);
  const validFormats: Array<{ start: number; end: number; type: string; content: string; url?: string }> = [];
  formats.forEach(format => {
    const overlaps = validFormats.some(f => 
      !(format.end <= f.start || format.start >= f.end)
    );
    if (!overlaps) {
      validFormats.push(format);
    }
  });
  
  // Reconstruir texto
  let lastIndex = 0;
  validFormats.forEach((format, idx) => {
    // Texto antes da formatação
    if (format.start > lastIndex) {
      const before = processed.substring(lastIndex, format.start);
      if (before) {
        result.push(before);
      }
    }
    
    // Texto formatado
    switch (format.type) {
      case 'bold':
        result.push(
          <Box key={`${baseKey}-bold-${idx}`} component="strong" sx={{ fontWeight: 700 }}>
            {format.content}
          </Box>
        );
        break;
      case 'italic':
        result.push(
          <Box key={`${baseKey}-italic-${idx}`} component="em" sx={{ fontStyle: 'italic' }}>
            {format.content}
          </Box>
        );
        break;
      case 'code':
        result.push(
          <Box
            key={`${baseKey}-code-${idx}`}
            component="span"
            sx={{
              backgroundColor: 'rgba(0, 0, 0, 0.08)',
              padding: '2px 6px',
              borderRadius: '4px',
              fontSize: '0.9em',
              fontFamily: '"Courier New", Courier, monospace',
              fontWeight: 500,
            }}
          >
            {format.content}
          </Box>
        );
        break;
      case 'strike':
        result.push(
          <Box
            key={`${baseKey}-strike-${idx}`}
            component="span"
            sx={{ textDecoration: 'line-through', opacity: 0.7 }}
          >
            {format.content}
          </Box>
        );
        break;
      case 'link':
        result.push(
          <Box
            key={`${baseKey}-link-${idx}`}
            component="a"
            href={format.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: '#2196f3',
              textDecoration: 'underline',
              '&:hover': { textDecoration: 'none' },
            }}
          >
            {format.content}
          </Box>
        );
        break;
    }
    
    lastIndex = format.end;
  });
  
  // Texto restante
  if (lastIndex < processed.length) {
    const remaining = processed.substring(lastIndex);
    if (remaining) {
      result.push(remaining);
    }
  }
  
  // Se não houve formatações, retornar texto original
  return result.length > 0 ? result : [text];
};

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: number;
  media?: Array<{ type: 'image' | 'video'; url: string; alt?: string }>;
}

const HomologationPage: React.FC = () => {
  const theme = useTheme();
  const { organizationId, agentId } = useParams<{ organizationId: string; agentId: string }>();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentText, setCommentText] = useState('');
  const [userName, setUserName] = useState('');
  const [showNameDialog, setShowNameDialog] = useState(false);
  const [selectedText, setSelectedText] = useState<string>('');
  const [selectedMessageId, setSelectedMessageId] = useState<string | null>(null);
  const [hoveredMessageId, setHoveredMessageId] = useState<string | null>(null);
  const [agentInfo, setAgentInfo] = useState<AgentInfo | null>(null);
  const [loadingAgentInfo, setLoadingAgentInfo] = useState(false);
  const [showClearDialog, setShowClearDialog] = useState(false);
  const [promptSuggestions, setPromptSuggestions] = useState<PromptSuggestion[]>([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  const [suggestionTextByPrompt, setSuggestionTextByPrompt] = useState<Record<number, string>>({});
  const [imageLinkByKey, setImageLinkByKey] = useState<Record<string, string>>({});
  const [uploadingImageKey, setUploadingImageKey] = useState<string | null>(null);
  const imageUploadInputRef = useRef<HTMLInputElement>(null);
  const [uploadTarget, setUploadTarget] = useState<{ promptIndex: number; originalUrl: string } | null>(null);
  const [pendingTextSuggestion, setPendingTextSuggestion] = useState<number | null>(null);
  const [pendingImageSuggestion, setPendingImageSuggestion] = useState<{ promptIndex: number; originalUrl: string; mode: 'upload' | 'link'; file?: File; link?: string } | null>(null);
  const [promptFilter, setPromptFilter] = useState('');
  const [filterPromptsWithImageOnly, setFilterPromptsWithImageOnly] = useState(false);
  const [suggestionFilter, setSuggestionFilter] = useState<'all' | 'text' | 'image'>('all');
  const [suggestionsSectionExpanded, setSuggestionsSectionExpanded] = useState(true);
  const [expandedPrompts, setExpandedPrompts] = useState<Set<number>>(new Set());
  const [imageZoomUrl, setImageZoomUrl] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const commentsContainerRef = useRef<HTMLDivElement>(null);
  const chatHistoryRef = useRef<HomologationChatMessage[]>([]);
  const commentInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (organizationId && agentId) {
      loadComments();
      loadAgentInfo();
      loadPromptSuggestions();
    }
  }, [organizationId, agentId]);

  const loadAgentInfo = async () => {
    if (!organizationId || !agentId) return;
    try {
      setLoadingAgentInfo(true);
      const info = await homologationChatApi.getAgentInfo(organizationId, agentId);
      setAgentInfo(info);
    } catch (err) {
      console.error('Erro ao carregar informações do agente:', err);
    } finally {
      setLoadingAgentInfo(false);
    }
  };

  useEffect(() => {
    if (messages.length > 0 && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [messages]);

  useEffect(() => {
    if (comments.length > 0 && commentsContainerRef.current) {
      commentsContainerRef.current.scrollTo({
        top: commentsContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    }
  }, [comments]);


  const loadComments = async () => {
    if (!organizationId || !agentId) return;
    try {
      const loadedComments = await homologationChatApi.getComments(organizationId, agentId);
      setComments(loadedComments);
    } catch (err) {
      console.error('Erro ao carregar comentários:', err);
    }
  };

  const loadPromptSuggestions = async (silentRefresh = false) => {
    if (!organizationId || !agentId) return;
    try {
      if (!silentRefresh) setLoadingSuggestions(true);
      const loaded = await homologationChatApi.getPromptSuggestions(organizationId, agentId);
      setPromptSuggestions(loaded);
    } catch (err) {
      console.error('Erro ao carregar sugestões de prompts:', err);
    } finally {
      if (!silentRefresh) setLoadingSuggestions(false);
    }
  };

  const handleAddTextSuggestion = async (promptIndex: number) => {
    const text = suggestionTextByPrompt[promptIndex]?.trim();
    if (!text || !organizationId || !agentId) return;
    if (!userName.trim()) {
      setPendingTextSuggestion(promptIndex);
      setShowNameDialog(true);
      return;
    }
    try {
      const newSuggestion = await homologationChatApi.addPromptSuggestion({
        organizationId,
        agentId,
        userName: userName.trim(),
        promptIndex,
        suggestionType: 'text',
        suggestedText: text,
      });
      setSuggestionTextByPrompt((prev) => ({ ...prev, [promptIndex]: '' }));
      setPendingTextSuggestion(null);
      setPromptSuggestions((prev) => [...prev, newSuggestion]);
      toast.success('Sugestão de texto enviada com sucesso!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar sugestão.');
    }
  };

  const handleAddImageSuggestionUpload = async (promptIndex: number, originalUrl: string, file: File) => {
    if (!userName.trim()) {
      setPendingImageSuggestion({ promptIndex, originalUrl, mode: 'upload', file });
      setShowNameDialog(true);
      return;
    }
    const key = `${promptIndex}-${originalUrl}`;
    setUploadingImageKey(key);
    try {
      const result = await fileUploadApi.uploadFile(file);
      const newSuggestion = await homologationChatApi.addPromptSuggestion({
        organizationId: organizationId!,
        agentId: agentId!,
        userName: userName.trim(),
        promptIndex,
        suggestionType: 'image',
        originalImageUrl: originalUrl,
        suggestedImageUrl: result.url,
        imageSource: 'upload',
      });
      setPromptSuggestions((prev) => [...prev, newSuggestion]);
      toast.success('Sugestão de imagem enviada com sucesso!');
      if (imageUploadInputRef.current) imageUploadInputRef.current.value = '';
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao fazer upload.');
    } finally {
      setUploadingImageKey(null);
    }
  };

  const handleAddImageSuggestionLink = async (promptIndex: number, originalUrl: string) => {
    const link = imageLinkByKey[`${promptIndex}-${originalUrl}`]?.trim();
    if (!link || !organizationId || !agentId) {
      toast.error('Informe a URL da imagem.');
      return;
    }
    if (!userName.trim()) {
      setPendingImageSuggestion({ promptIndex, originalUrl, mode: 'link', link });
      setShowNameDialog(true);
      return;
    }
    try {
      const newSuggestion = await homologationChatApi.addPromptSuggestion({
        organizationId,
        agentId,
        userName: userName.trim(),
        promptIndex,
        suggestionType: 'image',
        originalImageUrl: originalUrl,
        suggestedImageUrl: link,
        imageSource: 'link',
      });
      setImageLinkByKey((prev) => {
        const next = { ...prev };
        delete next[`${promptIndex}-${originalUrl}`];
        return next;
      });
      setPromptSuggestions((prev) => [...prev, newSuggestion]);
      toast.success('Sugestão de imagem enviada com sucesso!');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao enviar sugestão.');
    }
  };

  const handleImageUploadInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    const target = uploadTarget;
    e.target.value = '';
    if (!file || !target || !organizationId || !agentId) return;
    if (!userName.trim()) {
      setPendingImageSuggestion({ promptIndex: target.promptIndex, originalUrl: target.originalUrl, mode: 'upload', file });
      setShowNameDialog(true);
      setUploadTarget(null);
      return;
    }
    handleAddImageSuggestionUpload(target.promptIndex, target.originalUrl, file);
    setUploadTarget(null);
  };

  const triggerImageUpload = (promptIndex: number, originalUrl: string) => {
    setUploadTarget({ promptIndex, originalUrl });
    setTimeout(() => imageUploadInputRef.current?.click(), 0);
  };

  const scrollToPrompt = (promptIndex: number) => {
    setPromptFilter('');
    setTimeout(() => {
      const el = document.getElementById(`prompt-${promptIndex}`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const executePendingSuggestionAfterName = async () => {
    const name = userName.trim();
    if (!name) return;
    if (pendingTextSuggestion !== null) {
      const promptIndex = pendingTextSuggestion;
      const text = suggestionTextByPrompt[promptIndex]?.trim();
      if (text && organizationId && agentId) {
        try {
          const newSuggestion = await homologationChatApi.addPromptSuggestion({
            organizationId,
            agentId,
            userName: name,
            promptIndex,
            suggestionType: 'text',
            suggestedText: text,
          });
          setSuggestionTextByPrompt((prev) => ({ ...prev, [promptIndex]: '' }));
          setPromptSuggestions((prev) => [...prev, newSuggestion]);
          toast.success('Sugestão de texto enviada com sucesso!');
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Erro ao enviar sugestão.');
        }
      }
      setPendingTextSuggestion(null);
    } else if (pendingImageSuggestion) {
      const { promptIndex, originalUrl, mode, file, link } = pendingImageSuggestion;
      if (mode === 'upload' && file) {
        setUploadingImageKey(`${promptIndex}-${originalUrl}`);
        try {
          const result = await fileUploadApi.uploadFile(file);
          const newSuggestion = await homologationChatApi.addPromptSuggestion({
            organizationId: organizationId!,
            agentId: agentId!,
            userName: name,
            promptIndex,
            suggestionType: 'image',
            originalImageUrl: originalUrl,
            suggestedImageUrl: result.url,
            imageSource: 'upload',
          });
          setPromptSuggestions((prev) => [...prev, newSuggestion]);
          toast.success('Sugestão de imagem enviada com sucesso!');
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Erro ao fazer upload.');
        } finally {
          setUploadingImageKey(null);
        }
      } else if (mode === 'link' && link) {
        try {
          const newSuggestion = await homologationChatApi.addPromptSuggestion({
            organizationId: organizationId!,
            agentId: agentId!,
            userName: name,
            promptIndex,
            suggestionType: 'image',
            originalImageUrl: originalUrl,
            suggestedImageUrl: link,
            imageSource: 'link',
          });
          setImageLinkByKey((prev) => {
            const next = { ...prev };
            delete next[`${promptIndex}-${originalUrl}`];
            return next;
          });
          setPromptSuggestions((prev) => [...prev, newSuggestion]);
          toast.success('Sugestão de imagem enviada com sucesso!');
        } catch (err) {
          toast.error(err instanceof Error ? err.message : 'Erro ao enviar sugestão.');
        }
      }
      setPendingImageSuggestion(null);
    }
  };

  const selectMessageText = (messageId: string, messageContent: string) => {
    // Limpar qualquer seleção anterior
    window.getSelection()?.removeAllRanges();
    
    // Selecionar todo o texto da mensagem
    const textToSelect = messageContent.trim();
    setSelectedText(textToSelect);
    setSelectedMessageId(messageId);
    
    // Focar no campo de comentário após um pequeno delay para garantir que o estado foi atualizado
    setTimeout(() => {
      // Tentar focar no textarea dentro do TextField
      const textareaElement = document.querySelector('textarea[placeholder*="comentário"]') as HTMLTextAreaElement;
      if (textareaElement) {
        textareaElement.focus();
        textareaElement.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }, 200);
  };

  const handleMessageClick = (messageId: string, messageContent: string) => {
    // Se já está selecionada, desselecionar
    if (selectedMessageId === messageId) {
      setSelectedText('');
      setSelectedMessageId(null);
      window.getSelection()?.removeAllRanges();
    } else {
      // Selecionar o texto inteiro da mensagem
      selectMessageText(messageId, messageContent);
    }
  };

  const handleSend = async () => {
    const trimmed = inputValue.trim();
    if (!trimmed || sending || !organizationId || !agentId) return;

    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: trimmed,
      timestamp: Date.now(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInputValue('');
    setSending(true);
    setError(null);

    try {
      const result = await homologationChatApi.sendMessage({
        message: trimmed,
        chatHistory: chatHistoryRef.current,
        organizationId,
        agentId,
      });

      chatHistoryRef.current = result.updatedChatHistory;

      const processedParts = splitMessageContent(result.response);
      
      processedParts.forEach((part, index) => {
        const assistantMessage: ChatMessage = {
          id: `assistant-${Date.now()}-${index}`,
          role: 'assistant',
          content: part.text,
          timestamp: Date.now(),
          media: part.media,
        };
        setMessages((prev) => [...prev, assistantMessage]);
      });
    } catch (err: any) {
      setError(err.message || 'Erro ao enviar mensagem');
      console.error('Erro ao enviar mensagem:', err);
      setMessages((prev) => prev.filter(msg => msg.id !== userMessage.id));
    } finally {
      setSending(false);
    }
  };


  const handleAddComment = async () => {
    if (!commentText.trim() || !organizationId || !agentId) return;

    if (!userName.trim()) {
      setShowNameDialog(true);
      return;
    }

    try {
      const newComment = await homologationChatApi.addComment({
        organizationId,
        agentId,
        userName,
        text: commentText,
        selectedText: selectedText || undefined,
        messageId: selectedMessageId || undefined,
      });

      setComments((prev) => [...prev, newComment]);
      setCommentText('');
      setSelectedText('');
      setSelectedMessageId(null);
      toast.success('Comentário adicionado com sucesso!');
    } catch (err: any) {
      toast.error(err.message || 'Erro ao adicionar comentário');
      console.error('Erro ao adicionar comentário:', err);
    }
  };

  const handleClearChat = async () => {
    if (!organizationId || !agentId) return;
    setShowClearDialog(true);
  };

  const confirmClearChat = async () => {
    if (!organizationId || !agentId) return;
    try {
      await homologationChatApi.clearChatHistory(organizationId, agentId);
      setMessages([]);
      chatHistoryRef.current = [];
      setError(null);
      setSelectedText('');
      setSelectedMessageId(null);
      setShowClearDialog(false);
      toast.success('Chat limpo com sucesso!');
    } catch (err) {
      console.error('Erro ao limpar chat:', err);
      toast.error('Erro ao limpar o chat');
      setShowClearDialog(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCommentKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleAddComment();
    }
  };

  if (!organizationId || !agentId) {
    return (
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h6" color="error">
          Parâmetros inválidos
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: '#313131',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: '#313131',
          padding: 3,
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                component="img"
                src="/logo-branco.svg"
                alt="Lia Chat"
                sx={{
                  height: 50,
                  width: 'auto',
                  objectFit: 'contain',
                }}
              />
              <Box>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                  Homologação de Agente
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                  Ambiente de teste e avaliação
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Chip
                icon={<ChatIcon sx={{ color: 'white !important' }} />}
                label={`${messages.length} mensagens`}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)',
                }}
              />
              <Chip
                icon={<CommentIcon sx={{ color: 'white !important' }} />}
                label={`${comments.length} comentários`}
                sx={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: 'white',
                  fontWeight: 'bold',
                  backdropFilter: 'blur(10px)',
                }}
              />
            </Box>
          </Box>
          {loadingAgentInfo ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, py: 1 }}>
              <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.9)' }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Carregando informações do agente...
              </Typography>
            </Box>
          ) : agentInfo ? (
            <Box sx={{ 
              background: 'rgba(255, 255, 255, 0.15)', 
              borderRadius: 2, 
              padding: 2,
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar 
                  src="/logo-lia-chat.png"
                  sx={{ bgcolor: '#128c7e', width: 48, height: 48 }}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold', mb: 0.5 }}>
                    {agentInfo.name}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircleIcon sx={{ fontSize: 16, color: '#4caf50' }} />
                    <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                      Agente ativo e pronto para testes
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          ) : (
            <Box sx={{ 
              background: 'rgba(255, 255, 255, 0.1)', 
              borderRadius: 2, 
              padding: 2,
              textAlign: 'center',
            }}>
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
                Carregando informações do agente...
              </Typography>
            </Box>
          )}
        </Container>
      </Box>

      {/* Chat e Comentários Container */}
      <Container maxWidth="xl" sx={{ flex: 1, py: 3 }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 3,
            height: '100%',
          }}
        >
          {/* Chat Container */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 50%' },
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '600px', md: '700px' },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                background: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                height: '100%',
                maxHeight: '100%',
                position: 'relative',
              }}
            >
          {/* Chat Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
              color: 'white',
              padding: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 12px rgba(7, 94, 84, 0.3)',
              position: 'relative',
              flexShrink: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ position: 'relative' }}>
                <Avatar 
                  src="/logo-lia-chat.png"
                  sx={{ 
                    bgcolor: '#128c7e', 
                    width: 52, 
                    height: 52, 
                    border: '3px solid rgba(255, 255, 255, 0.4)',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
                  }}
                />
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: 0,
                    right: 0,
                    width: 16,
                    height: 16,
                    borderRadius: '50%',
                    background: '#4caf50',
                    border: '3px solid white',
                    boxShadow: '0 2px 6px rgba(76, 175, 80, 0.5)',
                  }}
                />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.1rem', mb: 0.5 }}>
                  {agentInfo?.name || 'Assistente IA'}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box
                    sx={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: '#4caf50',
                      boxShadow: '0 0 10px rgba(76, 175, 80, 0.8)',
                      animation: 'pulse 2s infinite',
                      '@keyframes pulse': {
                        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                        '50%': { opacity: 0.7, transform: 'scale(1.1)' },
                      },
                    }}
                  />
                  <Typography variant="body2" sx={{ opacity: 0.95, fontWeight: 500, fontSize: '0.85rem' }}>
                    Online agora
                  </Typography>
                </Box>
              </Box>
            </Box>
            <Tooltip title="Limpar histórico do chat" arrow>
              <IconButton
                onClick={handleClearChat}
                sx={{ 
                  color: 'white',
                  background: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    background: 'rgba(255, 255, 255, 0.25)',
                    transform: 'scale(1.1)',
                  },
                  transition: 'all 0.2s ease',
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Box>

          {/* Messages Area */}
          <Box
            ref={messagesContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              background: 'linear-gradient(to bottom, #f8f9fa 0%, #ffffff 50%, #f8f9fa 100%)',
              backgroundSize: '100% 200px',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '8px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.05)',
                borderRadius: '4px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(18, 140, 126, 0.3)',
                borderRadius: '4px',
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.5)',
                },
              },
            }}
          >
            {messages.length === 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  gap: 4,
                  px: 2,
                  py: 8,
                }}
              >
                <Box sx={{ textAlign: 'center', maxWidth: '500px' }}>
                  <Box
                    sx={{
                      width: 100,
                      height: 100,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mx: 'auto',
                      mb: 3,
                      boxShadow: '0 8px 24px rgba(7, 94, 84, 0.3)',
                      position: 'relative',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        inset: -4,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                        opacity: 0.2,
                        zIndex: -1,
                      },
                    }}
                  >
                    <Avatar 
                      src="/logo-lia-chat.png"
                      sx={{ 
                        width: 80, 
                        height: 80,
                        border: '4px solid white',
                      }}
                    />
                  </Box>
                  <Typography 
                    variant="h4" 
                    sx={{ 
                      color: '#2d3748',
                      fontWeight: 'bold',
                      mb: 1.5,
                      fontSize: '1.75rem',
                      background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    👋 Olá! Sou {agentInfo?.name || 'sua assistente de IA'}
                  </Typography>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'rgba(0,0,0,0.7)',
                      fontSize: '1.05rem',
                      lineHeight: 1.8,
                      mb: 3,
                    }}
                  >
                    Estou pronto para conversar! Envie uma mensagem para começar o teste de homologação.
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <Chip 
                      icon={<ChatIcon />}
                      label="Chat interativo" 
                      sx={{ 
                        background: 'rgba(7, 94, 84, 0.1)',
                        color: '#075e54',
                        fontWeight: 600,
                        border: '1px solid rgba(7, 94, 84, 0.2)',
                        height: 36,
                      }}
                    />
                    <Chip 
                      icon={<CommentIcon />}
                      label="Comentários" 
                      sx={{ 
                        background: 'rgba(7, 94, 84, 0.1)',
                        color: '#075e54',
                        fontWeight: 600,
                        border: '1px solid rgba(7, 94, 84, 0.2)',
                        height: 36,
                      }}
                    />
                    <Chip 
                      icon={<CheckCircleIcon />}
                      label="Homologação" 
                      sx={{ 
                        background: 'rgba(7, 94, 84, 0.1)',
                        color: '#075e54',
                        fontWeight: 600,
                        border: '1px solid rgba(7, 94, 84, 0.2)',
                        height: 36,
                      }}
                    />
                  </Box>
                </Box>
              </Box>
            )}

            {messages.map((message) => {
              const isSelected = selectedMessageId === message.id;
              const isHovered = hoveredMessageId === message.id;
              const isUser = message.role === 'user';
              
              return (
              <Box
                key={message.id}
                sx={{
                  display: 'flex',
                  justifyContent: isUser ? 'flex-end' : 'flex-start',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  mb: 0.5,
                }}
              >
                {!isUser && (
                  <Avatar 
                    src="/logo-lia-chat.png"
                    sx={{ 
                      bgcolor: '#128c7e',
                      width: 36, 
                      height: 36,
                      border: '2px solid rgba(18, 140, 126, 0.2)',
                      boxShadow: '0 2px 8px rgba(18, 140, 126, 0.2)',
                    }}
                  />
                )}
                <Box
                  data-message-id={message.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (e.detail === 2) return;
                    handleMessageClick(message.id, message.content);
                  }}
                  onMouseEnter={() => setHoveredMessageId(message.id)}
                  onMouseLeave={() => setHoveredMessageId(null)}
                  sx={{
                    maxWidth: '70%',
                    minWidth: '140px',
                    background: isSelected 
                      ? (isUser 
                          ? 'linear-gradient(135deg, #c8e6c9 0%, #a5d6a7 100%)' 
                          : 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)')
                      : (isUser 
                          ? 'linear-gradient(135deg, #dcf8c6 0%, #c8e6c9 100%)' 
                          : 'white'),
                    borderRadius: isUser ? '18px 18px 4px 18px' : '18px 18px 18px 4px',
                    padding: '14px 16px',
                    boxShadow: isSelected 
                      ? '0 4px 16px rgba(7, 94, 84, 0.4)' 
                      : isHovered
                        ? '0 4px 12px rgba(0,0,0,0.15)'
                        : '0 2px 8px rgba(0,0,0,0.08)',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    border: isSelected 
                      ? '2px solid #128c7e' 
                      : isHovered 
                        ? '1px solid rgba(18, 140, 126, 0.3)' 
                        : '1px solid rgba(0,0,0,0.05)',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: isSelected 
                        ? '0 6px 20px rgba(7, 94, 84, 0.5)' 
                        : '0 6px 16px rgba(0,0,0,0.2)',
                    },
                  }}
                >
                  {isSelected && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: 8,
                        background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                        color: 'white',
                        borderRadius: '12px',
                        padding: '4px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 2,
                        boxShadow: '0 2px 8px rgba(7, 94, 84, 0.4)',
                        animation: 'pulse 2s infinite',
                        '@keyframes pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.8, transform: 'scale(1.05)' },
                        },
                      }}
                    >
                      <CommentIcon sx={{ fontSize: '0.85rem' }} />
                      Selecionado
                    </Box>
                  )}
                  {!isSelected && isHovered && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -8,
                        right: 8,
                        background: 'rgba(18, 140, 126, 0.15)',
                        backdropFilter: 'blur(10px)',
                        color: '#128c7e',
                        borderRadius: '12px',
                        padding: '4px 10px',
                        fontSize: '0.7rem',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        zIndex: 2,
                        border: '1px solid rgba(18, 140, 126, 0.3)',
                        transition: 'all 0.2s ease',
                      }}
                    >
                      <CommentIcon sx={{ fontSize: '0.85rem' }} />
                      Clique para comentar
                    </Box>
                  )}
                  {/* Media Content */}
                  {message.media && message.media.length > 0 && (
                    <Box sx={{ mb: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {message.media.map((media, idx) => (
                        <Box key={idx}>
                          {media.type === 'image' ? (
                            <Box
                              component="img"
                              src={media.url}
                              alt={media.alt || 'Image'}
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '7.5px',
                                objectFit: 'cover',
                                cursor: 'pointer',
                                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                '&:hover': {
                                  opacity: 0.9,
                                },
                              }}
                              onClick={() => window.open(media.url, '_blank')}
                            />
                          ) : (
                            <Box
                              component="video"
                              src={media.url}
                              controls
                              sx={{
                                maxWidth: '100%',
                                maxHeight: '300px',
                                borderRadius: '7.5px',
                              }}
                            />
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Text Content */}
                  {message.content && (
                    <Box
                      component="div"
                      sx={{
                        wordBreak: 'break-word',
                        overflowWrap: 'break-word',
                        color: '#303030',
                        lineHeight: 1.75,
                        fontSize: '0.92rem',
                        maxWidth: '100%',
                        letterSpacing: '0.01em',
                        textAlign: 'left',
                        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                        padding: '2px 0',
                        '& > div': {
                          display: 'block !important',
                          width: '100% !important',
                        },
                        '& a': {
                          color: '#2196f3',
                          textDecoration: 'underline',
                          '&:hover': {
                            textDecoration: 'none',
                          },
                        },
                        '& strong': {
                          fontWeight: 700,
                        },
                        '& em': {
                          fontStyle: 'italic',
                        },
                      }}
                    >
                      {formatWhatsAppMessage(message.content)}
                    </Box>
                  )}

                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      textAlign: isUser ? 'right' : 'left',
                      mt: 1,
                      color: 'rgba(0,0,0,0.45)',
                      fontSize: '0.7rem',
                      fontWeight: 500,
                    }}
                  >
                    {new Date(message.timestamp).toLocaleTimeString('pt-BR', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </Typography>
                </Box>
                {isUser && (
                  <Avatar 
                    sx={{ 
                      bgcolor: '#128c7e',
                      width: 36, 
                      height: 36,
                      border: '2px solid rgba(18, 140, 126, 0.2)',
                      boxShadow: '0 2px 8px rgba(18, 140, 126, 0.2)',
                      fontSize: '1.2rem',
                    }}
                  >
                    👤
                  </Avatar>
                )}
              </Box>
            );
            })}

            {error && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #ffebee 0%, #ffcdd2 100%)',
                  color: '#c62828',
                  padding: 2.5,
                  borderRadius: '12px',
                  textAlign: 'center',
                  border: '2px solid #ef5350',
                  boxShadow: '0 4px 12px rgba(198, 40, 40, 0.25)',
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.95rem' }}>{error}</Typography>
              </Box>
            )}

            {sending && messages.length > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Avatar 
                  src="/logo-lia-chat.png"
                  sx={{ 
                    bgcolor: '#128c7e',
                    width: 40, 
                    height: 40, 
                    border: '2px solid rgba(18, 140, 126, 0.2)',
                    boxShadow: '0 2px 8px rgba(18, 140, 126, 0.2)',
                  }}
                />
                <Box
                  sx={{
                    background: 'white',
                    borderRadius: '18px 18px 18px 4px',
                    padding: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                    border: '1px solid rgba(0,0,0,0.05)',
                  }}
                >
                  <CircularProgress size={20} sx={{ color: '#128c7e' }} />
                  <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)', fontWeight: 500 }}>
                    {agentInfo?.name || 'Assistente'} está digitando...
                  </Typography>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>

          {/* Input Area */}
          <Box
            sx={{
              background: 'linear-gradient(to bottom, #ffffff 0%, #f8f9fa 100%)',
              padding: 2.5,
              display: 'flex',
              alignItems: 'flex-end',
              gap: 1.5,
              borderTop: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.03)',
              flexShrink: 0,
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Digite sua mensagem..."
              disabled={sending}
              variant="outlined"
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '24px',
                  background: 'white',
                  color: '#000',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                  '& fieldset': {
                    borderColor: 'rgba(18, 140, 126, 0.2)',
                    borderWidth: '1.5px',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(18, 140, 126, 0.4)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#128c7e',
                    borderWidth: '2px',
                    boxShadow: '0 0 0 3px rgba(18, 140, 126, 0.1)',
                  },
                  '& input': {
                    color: '#000',
                    padding: '12px 16px',
                  },
                  '& textarea': {
                    color: '#000',
                    padding: '12px 16px',
                  },
                },
                '& .MuiInputBase-input::placeholder': {
                  color: 'rgba(0,0,0,0.5)',
                  opacity: 1,
                },
              }}
            />
            <Tooltip title={inputValue.trim() ? "Enviar mensagem" : "Digite uma mensagem"} arrow>
              <span>
                <IconButton
                  onClick={handleSend}
                  disabled={!inputValue.trim() || sending}
                  sx={{
                    background: inputValue.trim() && !sending 
                      ? 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)' 
                      : 'rgba(0,0,0,0.06)',
                    color: 'white',
                    width: 52,
                    height: 52,
                    borderRadius: '50%',
                    '&:hover': {
                      background: inputValue.trim() && !sending
                        ? 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)'
                        : 'rgba(0,0,0,0.1)',
                      transform: 'scale(1.08) rotate(5deg)',
                    },
                    '&:disabled': {
                      background: 'rgba(0,0,0,0.06)',
                      color: 'rgba(0,0,0,0.25)',
                    },
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: inputValue.trim() && !sending 
                      ? '0 4px 12px rgba(7, 94, 84, 0.4)' 
                      : 'none',
                  }}
                >
                  {sending ? <CircularProgress size={22} sx={{ color: 'white' }} /> : <SendIcon />}
                </IconButton>
              </span>
            </Tooltip>
          </Box>
            </Paper>
          </Box>

          {/* Seção de Comentários */}
          <Box
            sx={{
              flex: { xs: '1 1 100%', md: '1 1 50%' },
              display: 'flex',
              flexDirection: 'column',
              height: { xs: '600px', md: '700px' },
            }}
          >
            <Paper
              elevation={0}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                borderRadius: 4,
                overflow: 'hidden',
                background: 'white',
                border: '1px solid rgba(0,0,0,0.08)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
                height: '100%',
                maxHeight: '100%',
                position: 'relative',
              }}
            >
          <Box
            sx={{
              background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
              color: 'white',
              padding: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              boxShadow: '0 4px 12px rgba(7, 94, 84, 0.3)',
              position: 'relative',
              flexShrink: 0,
              '&::after': {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: 0,
                right: 0,
                height: '1px',
                background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <CommentIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem', mb: 0.25 }}>
                  Comentários de Homologação
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                  Avaliações e feedback sobre o agente
                </Typography>
              </Box>
            </Box>
            {comments.length > 0 && (
              <Chip
                label={comments.length}
                size="medium"
                sx={{
                  background: 'rgba(255, 255, 255, 0.25)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: '0.9rem',
                  height: 36,
                  border: '1px solid rgba(255, 255, 255, 0.3)',
                }}
              />
            )}
          </Box>

          <Box
            ref={commentsContainerRef}
            sx={{
              flex: 1,
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: { xs: 2.5, md: 3.5 },
              display: 'flex',
              flexDirection: 'column',
              gap: 3,
              background: 'linear-gradient(to bottom, #fafafa 0%, #ffffff 50%, #fafafa 100%)',
              backgroundSize: '100% 200px',
              minHeight: 0,
              '&::-webkit-scrollbar': {
                width: '10px',
              },
              '&::-webkit-scrollbar-track': {
                background: 'rgba(0,0,0,0.05)',
                borderRadius: '5px',
              },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(18, 140, 126, 0.3)',
                borderRadius: '5px',
                '&:hover': {
                  background: 'rgba(18, 140, 126, 0.5)',
                },
              },
            }}
          >
            {comments.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Box
                  sx={{
                    width: 80,
                    height: 80,
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mx: 'auto',
                    mb: 3,
                    boxShadow: '0 8px 24px rgba(7, 94, 84, 0.3)',
                  }}
                >
                  <CommentIcon sx={{ fontSize: 40, color: 'white' }} />
                </Box>
                <Typography variant="h6" sx={{ color: '#2d3748', fontWeight: 600, mb: 1 }}>
                  Nenhum comentário ainda
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', maxWidth: '400px', mx: 'auto', lineHeight: 1.7 }}>
                  Selecione um texto no chat acima para comentar ou adicione um comentário geral abaixo
                </Typography>
              </Box>
            ) : (
              comments.map((comment, index) => (
                <Box
                  key={comment.id}
                  sx={{
                    background: 'white',
                    borderRadius: '16px',
                    padding: 3,
                    border: '1px solid rgba(0,0,0,0.06)',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    '&:hover': {
                      boxShadow: '0 8px 24px rgba(7, 94, 84, 0.15)',
                      transform: 'translateY(-4px)',
                      borderColor: 'rgba(18, 140, 126, 0.2)',
                    },
                    '&::before': {
                      content: '""',
                      position: 'absolute',
                      left: 0,
                      top: 0,
                      bottom: 0,
                      width: '4px',
                      background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
                      borderRadius: '16px 0 0 16px',
                    },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Avatar 
                      sx={{ 
                        bgcolor: '#128c7e',
                        width: 48, 
                        height: 48,
                        fontWeight: 'bold',
                        fontSize: '1.1rem',
                        boxShadow: '0 4px 12px rgba(18, 140, 126, 0.3)',
                        border: '2px solid rgba(18, 140, 126, 0.2)',
                      }}
                    >
                      {comment.userName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', color: '#000000', fontSize: '0.95rem' }}>
                          {comment.userName}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(0,0,0,0.5)', fontSize: '0.75rem', fontWeight: 500 }}>
                          {new Date(comment.timestamp).toLocaleString('pt-BR', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </Typography>
                      </Box>
                      {comment.selectedText && (
                        <Box
                          sx={{
                            background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
                            borderLeft: '4px solid #ffc107',
                            padding: 2,
                            borderRadius: '8px',
                            mb: 2,
                            mt: 1.5,
                            boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)',
                          }}
                        >
                          <Typography variant="caption" sx={{ 
                            fontWeight: 'bold', 
                            color: '#856404',
                            display: 'block',
                            mb: 1,
                            fontSize: '0.7rem',
                            textTransform: 'uppercase',
                            letterSpacing: '0.8px',
                          }}>
                            📌 Texto Referenciado
                          </Typography>
                          <Typography variant="body2" sx={{ 
                            fontStyle: 'italic', 
                            color: '#856404',
                            lineHeight: 1.7,
                            fontSize: '0.9rem',
                          }}>
                            "{comment.selectedText}"
                          </Typography>
                        </Box>
                      )}
                      <Typography 
                        variant="body1" 
                        sx={{ 
                          color: '#000000',
                          lineHeight: 1.8,
                          whiteSpace: 'pre-wrap',
                          fontSize: '0.95rem',
                        }}
                      >
                        {comment.text}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))
            )}
          </Box>

          <Box
            sx={{
              borderTop: '1px solid rgba(0,0,0,0.06)',
              padding: { xs: 2.5, md: 3.5 },
              background: 'linear-gradient(to bottom, #ffffff 0%, #fafafa 100%)',
              boxShadow: '0 -2px 8px rgba(0,0,0,0.03)',
              flexShrink: 0,
            }}
          >
            {selectedText && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                  border: '2px solid #128c7e',
                  borderRadius: '12px',
                  padding: 2.5,
                  mb: 2.5,
                  boxShadow: '0 4px 16px rgba(18, 140, 126, 0.25)',
                  animation: 'slideIn 0.3s ease',
                  '@keyframes slideIn': {
                    '0%': { opacity: 0, transform: 'translateY(-10px)' },
                    '100%': { opacity: 1, transform: 'translateY(0)' },
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CommentIcon sx={{ fontSize: '1.2rem', color: '#128c7e' }} />
                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#128c7e', fontSize: '0.75rem', letterSpacing: '0.5px' }}>
                      TEXTO SELECIONADO PARA COMENTAR
                    </Typography>
                  </Box>
                  <Tooltip title="Remover seleção" arrow>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSelectedText('');
                        setSelectedMessageId(null);
                        window.getSelection()?.removeAllRanges();
                      }}
                      sx={{ 
                        color: '#128c7e',
                        background: 'rgba(18, 140, 126, 0.1)',
                        '&:hover': {
                          background: 'rgba(18, 140, 126, 0.2)',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    background: 'white',
                    borderRadius: '8px',
                    padding: 2,
                    border: '1px solid rgba(18, 140, 126, 0.2)',
                    maxHeight: '120px',
                    overflowY: 'auto',
                  }}
                >
                  <Typography variant="body2" sx={{ fontStyle: 'italic', color: '#424242', lineHeight: 1.7 }}>
                    "{selectedText}"
                  </Typography>
                </Box>
              </Box>
            )}
            {!selectedText && (
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #fff3cd 0%, #ffe69c 100%)',
                  borderLeft: '4px solid #ffc107',
                  padding: 2,
                  mb: 2.5,
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  boxShadow: '0 2px 8px rgba(255, 193, 7, 0.15)',
                }}
              >
                <InfoIcon sx={{ fontSize: '1.4rem', color: '#856404' }} />
                <Typography variant="body2" sx={{ color: '#856404', fontSize: '0.85rem', lineHeight: 1.6 }}>
                  <strong>Dica:</strong> Clique em uma mensagem acima para selecioná-la e comentar, ou adicione um comentário geral abaixo.
                </Typography>
              </Box>
            )}
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
              <TextField
                inputRef={commentInputRef}
                fullWidth
                multiline
                maxRows={4}
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onKeyDown={handleCommentKeyDown}
                placeholder={selectedText ? "Adicione seu comentário sobre o texto selecionado..." : "Adicione um comentário sobre o agente..."}
                variant="outlined"
                size="medium"
                autoFocus={!!selectedText}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: 'white',
                    borderRadius: '16px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                    '&:focus-within': {
                      borderColor: '#128c7e',
                      borderWidth: '2px',
                      boxShadow: '0 0 0 3px rgba(18, 140, 126, 0.1)',
                    },
                    '& fieldset': {
                      borderColor: selectedText ? '#128c7e' : 'rgba(18, 140, 126, 0.2)',
                      borderWidth: '1.5px',
                    },
                    '&:hover fieldset': {
                      borderColor: 'rgba(18, 140, 126, 0.4)',
                    },
                    '& input': {
                      padding: '12px 16px',
                      color: '#000000',
                    },
                    '& textarea': {
                      padding: '12px 16px',
                      color: '#000000',
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(0,0,0,0.5)',
                    opacity: 1,
                  },
                }}
              />
              <Tooltip title={commentText.trim() ? "Enviar comentário" : "Digite um comentário"} arrow>
                <span>
                  <IconButton
                    onClick={handleAddComment}
                    disabled={!commentText.trim()}
                    sx={{
                      background: commentText.trim() 
                        ? 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)' 
                        : 'rgba(0,0,0,0.06)',
                      color: 'white',
                      width: 52,
                      height: 52,
                      borderRadius: '50%',
                      '&:hover': {
                        background: commentText.trim() 
                          ? 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)' 
                          : 'rgba(0,0,0,0.1)',
                        transform: 'scale(1.08) rotate(5deg)',
                      },
                      '&:disabled': {
                        background: 'rgba(0,0,0,0.06)',
                        color: 'rgba(0,0,0,0.25)',
                      },
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      boxShadow: commentText.trim() 
                        ? '0 4px 12px rgba(7, 94, 84, 0.4)' 
                        : 'none',
                    }}
                  >
                    <SendIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>
            </Paper>
          </Box>
        </Box>
      </Container>

      {/* Melhorias em Prompts */}
      <Container maxWidth="xl" sx={{ py: 3 }}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 4,
            overflow: 'hidden',
            background: 'white',
            border: '1px solid rgba(0,0,0,0.08)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            height: '80vh',
            minHeight: 500,
          }}
        >
          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
            }}
          >
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                background: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)',
                color: 'white',
                padding: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                boxShadow: '0 4px 12px rgba(57, 73, 171, 0.3)',
                flexShrink: 0,
              }}
            >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: '12px',
                  background: 'rgba(255, 255, 255, 0.25)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                }}
              >
                <AutoFixHighIcon sx={{ fontSize: 28 }} />
              </Box>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: '1.2rem', mb: 0.25 }}>
                  Melhorias em Prompts
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9, fontSize: '0.8rem' }}>
                  Sugira alterações nos textos e imagens dos prompts do agente
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              {promptSuggestions.length > 0 && (
                <Chip
                  label={promptSuggestions.length}
                  size="medium"
                  sx={{
                    background: 'rgba(255, 255, 255, 0.25)',
                    color: 'white',
                    fontWeight: 'bold',
                    fontSize: '0.9rem',
                    height: 36,
                    border: '1px solid rgba(255, 255, 255, 0.3)',
                  }}
                />
              )}
              <TextField
                size="small"
                placeholder="Filtrar prompts..."
                value={promptFilter}
                onChange={(e) => setPromptFilter(e.target.value)}
                sx={{
                  minWidth: { xs: 140, md: 220 },
                  '& .MuiOutlinedInput-root': {
                    background: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    borderRadius: 2,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                    '&:hover fieldset': { borderColor: 'rgba(255, 255, 255, 0.8)' },
                    '& input::placeholder': { color: 'rgba(255, 255, 255, 0.8)', opacity: 1 },
                  },
                }}
              />
              <Tooltip title="Exibir apenas prompts que contêm imagem (png, jpg, etc.)">
                <Chip
                  icon={<ImageIcon sx={{ fontSize: 18, color: 'inherit' }} />}
                  label="Com imagem"
                  size="small"
                  onClick={() => setFilterPromptsWithImageOnly((v) => !v)}
                  sx={{
                    height: 32,
                    fontSize: '0.8rem',
                    background: filterPromptsWithImageOnly ? 'rgba(255, 255, 255, 0.35)' : 'rgba(255, 255, 255, 0.15)',
                    color: 'white',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    cursor: 'pointer',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              </Tooltip>
            </Box>
          </Box>

            <Box
              sx={{
                flex: 1,
                padding: { xs: 2.5, md: 3.5 },
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                background: 'linear-gradient(to bottom, #fafafa 0%, #ffffff 50%, #fafafa 100%)',
              }}
            >
            {loadingSuggestions ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress sx={{ color: '#3949ab' }} />
              </Box>
            ) : !agentInfo?.prompts?.length ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <AutoFixHighIcon sx={{ fontSize: 48, color: 'rgba(0,0,0,0.2)', mb: 2 }} />
                <Typography variant="body1" sx={{ color: 'rgba(0,0,0,0.6)' }}>
                  Nenhum prompt configurado neste agente.
                </Typography>
              </Box>
            ) : (
              <>
                {/* Sugestões enviadas - em primeiro, com collapse */}
                {promptSuggestions.length > 0 && (
                  <Box sx={{ mb: 3 }}>
                    <Box
                      onClick={() => setSuggestionsSectionExpanded((e) => !e)}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        flexWrap: 'wrap',
                        gap: 2,
                        p: 2,
                        background: 'linear-gradient(135deg, #5c6bc0 0%, #3949ab 100%)',
                        color: 'white',
                        borderRadius: 2,
                        cursor: 'pointer',
                        boxShadow: '0 2px 8px rgba(57, 73, 171, 0.3)',
                        '&:hover': { opacity: 0.95 },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                          Sugestões enviadas
                        </Typography>
                        <Chip label={promptSuggestions.length} size="small" sx={{ background: 'rgba(255,255,255,0.3)', color: 'white', fontWeight: 'bold' }} />
                        {suggestionsSectionExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </Box>
                      {suggestionsSectionExpanded && (
                        <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                          <Button size="small" variant={suggestionFilter === 'all' ? 'contained' : 'outlined'} onClick={() => setSuggestionFilter('all')} sx={suggestionFilter === 'all' ? { background: 'rgba(255,255,255,0.3)', color: 'white' } : { borderColor: 'rgba(255,255,255,0.7)', color: 'white' }}>Todas</Button>
                          <Button size="small" variant={suggestionFilter === 'text' ? 'contained' : 'outlined'} onClick={() => setSuggestionFilter('text')} sx={suggestionFilter === 'text' ? { background: 'rgba(255,255,255,0.3)', color: 'white' } : { borderColor: 'rgba(255,255,255,0.7)', color: 'white' }}>Texto</Button>
                          <Button size="small" variant={suggestionFilter === 'image' ? 'contained' : 'outlined'} onClick={() => setSuggestionFilter('image')} sx={suggestionFilter === 'image' ? { background: 'rgba(255,255,255,0.3)', color: 'white' } : { borderColor: 'rgba(255,255,255,0.7)', color: 'white' }}>Imagem</Button>
                        </Box>
                      )}
                    </Box>
                    {suggestionsSectionExpanded && (
                      <Box sx={{ mt: 1.5 }}>
                        {promptSuggestions
                          .filter((sugg) => suggestionFilter === 'all' || sugg.suggestionType === suggestionFilter)
                          .map((sugg) => (
                          <Box
                            key={sugg.id}
                            sx={{
                              background: 'white',
                              borderRadius: 2,
                              padding: 2,
                              mb: 1.5,
                              border: '1px solid rgba(0,0,0,0.06)',
                              borderLeft: '4px solid #3949ab',
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#3949ab' }}>{sugg.userName}</Typography>
                                <Typography variant="caption" sx={{ color: '#999' }}>• Prompt {sugg.promptIndex + 1} • {sugg.suggestionType === 'text' ? 'Texto' : 'Imagem'}{sugg.imageSource && ` (${sugg.imageSource === 'upload' ? 'upload' : 'link'})`}</Typography>
                                <Chip label={sugg.status} size="small" sx={{ fontSize: '0.7rem', height: 20 }} />
                              </Box>
                              <Button size="small" startIcon={<LaunchIcon fontSize="small" />} onClick={() => scrollToPrompt(sugg.promptIndex)} sx={{ color: '#3949ab', fontSize: '0.75rem' }}>Ir ao prompt</Button>
                            </Box>
                            {sugg.suggestionType === 'text' && sugg.suggestedText && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 0.5 }}>Texto sugerido:</Typography>
                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#333' }}>{sugg.suggestedText}</Typography>
                              </Box>
                            )}
                            {sugg.suggestionType === 'image' && (
                              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                {sugg.originalImageUrl && (
                                  <Box>
                                    <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 0.5 }}>Imagem original (a substituir):</Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                                      <Box component="img" src={sugg.originalImageUrl} alt="Imagem original" sx={{ maxWidth: 120, maxHeight: 120, borderRadius: 1, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.1)' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                      <Typography variant="caption" sx={{ color: '#666', wordBreak: 'break-all', maxWidth: 280 }}>{sugg.originalImageUrl}</Typography>
                                    </Box>
                                  </Box>
                                )}
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#3949ab', display: 'block', mb: 0.5 }}>Sugestão de substituição:</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, flexWrap: 'wrap' }}>
                                    {/\.(jpg|jpeg|png|gif|webp|bmp|svg)$/i.test(sugg.suggestedImageUrl || '') || (sugg.suggestedImageUrl?.startsWith('http') && !sugg.suggestedImageUrl.includes(' ')) ? (
                                      <>
                                        <Box component="img" src={sugg.suggestedImageUrl} alt="Sugestão" sx={{ maxWidth: 120, maxHeight: 120, borderRadius: 1, objectFit: 'cover', border: '1px solid rgba(0,0,0,0.1)' }} onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                                        <Typography variant="caption" sx={{ color: '#666', wordBreak: 'break-all', maxWidth: 280 }}>{sugg.suggestedImageUrl}</Typography>
                                      </>
                                    ) : (
                                      <Typography variant="body2" sx={{ color: '#333', wordBreak: 'break-all' }}>{sugg.suggestedImageUrl || '(link ou URL informada)'}</Typography>
                                    )}
                                  </Box>
                                </Box>
                              </Box>
                            )}
                            <Typography variant="caption" sx={{ color: '#999', display: 'block', mt: 1.5 }}>{new Date(sugg.timestamp).toLocaleString('pt-BR')}</Typography>
                          </Box>
                        ))}
                        {promptSuggestions.filter((s) => suggestionFilter === 'all' || s.suggestionType === suggestionFilter).length === 0 && (
                          <Typography variant="body2" sx={{ color: '#999', py: 2 }}>Nenhuma sugestão {suggestionFilter === 'text' ? 'de texto' : suggestionFilter === 'image' ? 'de imagem' : ''} encontrada.</Typography>
                        )}
                      </Box>
                    )}
                  </Box>
                )}

                {/* Lista de prompts - cada um com collapse */}
                {(() => {
                  const query = promptFilter.trim().toLowerCase();
                  const promptsList = agentInfo.prompts || [];
                  let filtered = query
                    ? promptsList.reduce<{ prompt: { role: string; content: string }; originalIndex: number }[]>((acc, p, i) => {
                        const role = (p.role || '').toLowerCase();
                        const content = (p.content || '').toLowerCase();
                        if (role.includes(query) || content.includes(query)) acc.push({ prompt: p, originalIndex: i });
                        return acc;
                      }, [])
                    : promptsList.map((p, i) => ({ prompt: p, originalIndex: i }));
                  if (filterPromptsWithImageOnly) {
                    filtered = filtered.filter(({ prompt }) =>
                      detectFilesInText(prompt.content).some((f) => f.fileType === 'image')
                    );
                  }
                  const PREVIEW_LENGTH = 80;
                  return filtered.map(({ prompt, originalIndex: promptIndex }) => {
                  const detectedImages = detectFilesInText(prompt.content).filter((f) => f.fileType === 'image');
                  const isExpanded = expandedPrompts.has(promptIndex);
                  const preview = (prompt.content || '').trim();
                  const previewText = preview.length <= PREVIEW_LENGTH ? preview : preview.slice(0, PREVIEW_LENGTH) + '...';
                  const imageExtensions = Array.from(new Set(detectedImages.map((f) => (f.originalUrl.split('.').pop() || '').split(/[#?]/)[0].toLowerCase()))).filter(Boolean);
                  return (
                    <Box
                      key={promptIndex}
                      id={`prompt-${promptIndex}`}
                      sx={{
                        background: 'white',
                        borderRadius: 2,
                        border: '1px solid rgba(0,0,0,0.06)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                        scrollMarginTop: 16,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        onClick={() => setExpandedPrompts((prev) => {
                          const next = new Set(prev);
                          if (next.has(promptIndex)) next.delete(promptIndex);
                          else next.add(promptIndex);
                          return next;
                        })}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                          p: 2,
                          cursor: 'pointer',
                          '&:hover': { background: 'rgba(57, 73, 171, 0.06)' },
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Typography variant="caption" sx={{ color: '#3949ab', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: 1, display: 'block' }}>
                              Prompt {promptIndex + 1} {prompt.role ? `(${prompt.role})` : ''}
                            </Typography>
                            {detectedImages.length > 0 && (
                              <Tooltip title={imageExtensions.length ? `Contém ${detectedImages.length} imagem(ns): ${imageExtensions.join(', ').toUpperCase()}` : `Contém ${detectedImages.length} imagem(ns)`}>
                                <Chip
                                  icon={<ImageIcon sx={{ fontSize: 16 }} />}
                                  label={`${detectedImages.length} imagem(ns)${imageExtensions.length ? ` · ${imageExtensions.join(', ').toUpperCase()}` : ''}`}
                                  size="small"
                                  sx={{
                                    height: 22,
                                    fontSize: '0.7rem',
                                    '& .MuiChip-icon': { color: '#3949ab' },
                                    borderColor: '#3949ab',
                                    color: '#3949ab',
                                    background: 'rgba(57, 73, 171, 0.08)',
                                  }}
                                  variant="outlined"
                                />
                              </Tooltip>
                            )}
                          </Box>
                          <Typography variant="body2" sx={{ color: '#666', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', mt: 0.5 }}>
                            {previewText || '(vazio)'}
                          </Typography>
                        </Box>
                        {isExpanded ? <ExpandLessIcon sx={{ color: '#3949ab', flexShrink: 0 }} /> : <ExpandMoreIcon sx={{ color: '#3949ab', flexShrink: 0 }} />}
                      </Box>
                      {isExpanded && (
                        <Box sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#333', lineHeight: 1.7, mb: 2 }}>
                        {prompt.content}
                      </Typography>

                      {detectedImages.length > 0 && (
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
                            Imagens no prompt
                          </Typography>
                          {detectedImages.map((img, imgIdx) => {
                            const key = `${promptIndex}-${img.originalUrl}`;
                            const isUploading = uploadingImageKey === key;
                            return (
                              <Box
                                key={imgIdx}
                                sx={{
                                  display: 'flex',
                                  flexDirection: { xs: 'column', md: 'row' },
                                  gap: 2,
                                  alignItems: 'flex-start',
                                  mb: 2,
                                  p: 2,
                                  background: '#f5f5f5',
                                  borderRadius: 2,
                                  border: '1px solid rgba(0,0,0,0.08)',
                                }}
                              >
                                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                  <Box
                                    component="img"
                                    src={img.originalUrl}
                                    alt="Imagem do prompt"
                                    onError={(e) => {
                                      (e.target as HTMLImageElement).style.display = 'none';
                                    }}
                                    onClick={() => setImageZoomUrl(img.originalUrl)}
                                    sx={{
                                      maxWidth: 120,
                                      maxHeight: 120,
                                      borderRadius: 1,
                                      objectFit: 'cover',
                                      border: '1px solid rgba(0,0,0,0.1)',
                                      cursor: 'pointer',
                                      '&:hover': { opacity: 0.9 },
                                    }}
                                  />
                                  <Tooltip title="Ampliar imagem">
                                    <IconButton
                                      size="small"
                                      onClick={(e) => { e.stopPropagation(); setImageZoomUrl(img.originalUrl); }}
                                      sx={{
                                        position: 'absolute',
                                        bottom: 4,
                                        right: 4,
                                        background: 'rgba(0,0,0,0.5)',
                                        color: 'white',
                                        '&:hover': { background: 'rgba(0,0,0,0.7)' },
                                      }}
                                    >
                                      <ZoomInIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                </Box>
                                <Box sx={{ flex: 1, width: '100%' }}>
                                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#3949ab', display: 'block', mb: 1.5 }}>
                                    Sugerir troca desta imagem
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 1.5 }}>
                                    Escolha uma das opções:
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                      <input
                                        ref={imageUploadInputRef}
                                        type="file"
                                        accept=".jpg,.jpeg,.png,.gif,.webp"
                                        style={{ display: 'none' }}
                                        onChange={handleImageUploadInputChange}
                                      />
                                      <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={isUploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
                                        disabled={isUploading}
                                        onClick={() => triggerImageUpload(promptIndex, img.originalUrl)}
                                        sx={{ borderColor: '#3949ab', color: '#3949ab' }}
                                      >
                                        Enviar imagem por upload (CDN)
                                      </Button>
                                      <Typography variant="caption" sx={{ color: '#999' }}>ou</Typography>
                                      <TextField
                                        size="small"
                                        placeholder="Colar URL da imagem"
                                        value={imageLinkByKey[key] || ''}
                                        onChange={(e) => setImageLinkByKey((prev) => ({ ...prev, [key]: e.target.value }))}
                                        sx={{ minWidth: 200, '& .MuiInputBase-root': { fontSize: '0.85rem' } }}
                                      />
                                      <Button
                                        variant="contained"
                                        size="small"
                                        onClick={() => handleAddImageSuggestionLink(promptIndex, img.originalUrl)}
                                        disabled={!imageLinkByKey[key]?.trim()}
                                        sx={{ background: '#3949ab', '&:hover': { background: '#303f9f' } }}
                                      >
                                        Usar link
                                      </Button>
                                    </Box>
                                  </Box>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      )}

                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption" sx={{ fontWeight: 'bold', color: '#666', display: 'block', mb: 1 }}>
                          Sugerir alteração do texto
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
                          <TextField
                            fullWidth
                            multiline
                            maxRows={4}
                            placeholder="Digite sua sugestão de alteração..."
                            value={suggestionTextByPrompt[promptIndex] || ''}
                            onChange={(e) => setSuggestionTextByPrompt((prev) => ({ ...prev, [promptIndex]: e.target.value }))}
                            variant="outlined"
                            size="small"
                            sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                          />
                          <Button
                            variant="contained"
                            onClick={() => handleAddTextSuggestion(promptIndex)}
                            disabled={!suggestionTextByPrompt[promptIndex]?.trim()}
                            sx={{ background: '#3949ab', '&:hover': { background: '#303f9f' } }}
                          >
                            Enviar
                          </Button>
                        </Box>
                      </Box>
                        </Box>
                      )}
                    </Box>
                  );
                });
                })()}
              </>
            )}
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* Modal de zoom da imagem */}
      <Dialog
        open={!!imageZoomUrl}
        onClose={() => setImageZoomUrl(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            maxWidth: '95vw',
            maxHeight: '95vh',
            background: 'transparent',
            boxShadow: 'none',
          },
        }}
        BackdropProps={{ sx: { background: 'rgba(0,0,0,0.85)' } }}
      >
        {imageZoomUrl && (
          <>
            <IconButton
              onClick={() => setImageZoomUrl(null)}
              sx={{
                position: 'absolute',
                top: 8,
                right: 8,
                zIndex: 1,
                color: 'white',
                background: 'rgba(0,0,0,0.5)',
                '&:hover': { background: 'rgba(0,0,0,0.7)' },
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={imageZoomUrl}
              alt="Imagem em tamanho maior"
              onClick={() => setImageZoomUrl(null)}
              sx={{
                maxWidth: '90vw',
                maxHeight: '90vh',
                objectFit: 'contain',
                cursor: 'pointer',
              }}
            />
          </>
        )}
      </Dialog>

      {/* Dialog para nome do usuário */}
      <Dialog 
        open={showNameDialog} 
        onClose={() => {
          setShowNameDialog(false);
          setPendingTextSuggestion(null);
          setPendingImageSuggestion(null);
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px',
          },
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)',
          color: 'white',
          fontWeight: 'bold',
          pb: 2,
        }}>
          Informe seu nome
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.6)', mb: 2 }}>
            {(pendingTextSuggestion !== null || pendingImageSuggestion) 
              ? 'Para enviar sugestões de prompts, precisamos saber seu nome.' 
              : 'Para adicionar comentários, precisamos saber seu nome.'}
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Seu nome"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && userName.trim()) {
                setShowNameDialog(false);
                if (pendingTextSuggestion !== null || pendingImageSuggestion) {
                  executePendingSuggestionAfterName();
                } else {
                  handleAddComment();
                }
              }
            }}
            variant="outlined"
            sx={{ 
              mt: 1,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => {
              setShowNameDialog(false);
              setPendingTextSuggestion(null);
              setPendingImageSuggestion(null);
            }}
            sx={{ borderRadius: 2 }}
          >
            Cancelar
          </Button>
          <Button
            onClick={async () => {
              if (userName.trim()) {
                setShowNameDialog(false);
                if (pendingTextSuggestion !== null || pendingImageSuggestion) {
                  await executePendingSuggestionAfterName();
                  setPendingTextSuggestion(null);
                  setPendingImageSuggestion(null);
                } else {
                  handleAddComment();
                }
              }
            }}
            variant="contained"
            disabled={!userName.trim()}
            sx={{
              background: 'linear-gradient(135deg, #128c7e 0%, #075e54 100%)',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #075e54 0%, #128c7e 100%)',
              },
            }}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação para limpar chat */}
      <Dialog 
        open={showClearDialog} 
        onClose={() => setShowClearDialog(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            minWidth: '400px',
            background: 'white',
          },
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
          color: 'white',
          fontWeight: 'bold',
          pb: 2,
        }}>
          Confirmar limpeza do chat
        </DialogTitle>
        <DialogContent sx={{ pt: 5, background: 'white' }}>
          <Typography variant="body1" sx={{ color: '#000000', mt: 2, mb: 1, fontWeight: 500 }}>
            Tem certeza que deseja limpar todo o histórico do chat?
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(0,0,0,0.7)' }}>
            Esta ação não pode ser desfeita. Todos as mensagens serão removidas permanentemente.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button 
            onClick={() => setShowClearDialog(false)}
            sx={{ borderRadius: 2, color: '#000' }}
          >
            Cancelar
          </Button>
          <Button
            onClick={confirmClearChat}
            variant="contained"
            sx={{
              background: 'linear-gradient(135deg, #d32f2f 0%, #c62828 100%)',
              borderRadius: 2,
              px: 3,
              '&:hover': {
                background: 'linear-gradient(135deg, #c62828 0%, #d32f2f 100%)',
              },
            }}
          >
            Limpar Chat
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default HomologationPage;
